import React, { useRef, useMemo, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import Filter from '@/pages/intelligence/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import useUpdateFiltersFormValue from './_hooks/useUpdateFiltersFormValue';
import useUpdateFiltersCfg from './_hooks/useUpdateFiltersCfg';
import { getComposedChangedApiValue } from './utils';
import {
  // 时间筛选器
  RANGE_TIME as FILTER_RANGE_TIME,
  // 组合下拉筛选器
  COMPOSED_SELECT as FILTER_COMPOSED_SELECT,
  // 单选、多选国家筛选器
  COMPOSED_COUNTRY_SELECT as FILTER_COMPOSED_COUNTRY_SELECT,
  // 单地区筛选器
  SINGLE_AREA_SELECT as SINGLE_AREA_SELECT_FILTER,
  // 多选区域筛选器
  MULTIPLE_AREA_SELECT as FILTER_MULTIPLE_AREA_SELECT,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { FILTER_DEFAULT_INPUT_LIST, FILTER_DEFAULT_VALUE } from './_config';

import type { IBaseProps, IRegion } from '@/pages/intelligence/types/common';
import type { IStoreRankDetailsFilter } from '../../types';
import { nitendoCountryCodes, nitendoUSCountryCodes } from '@/pages/intelligence/_config/storeRank/console';

interface IPCStoreRankDetailsFilterProps extends IBaseProps {
  regionsOptions: IRegion[];
  regionsOptionsMap: { [key: string]: IRegion[]; };
  filter: IStoreRankDetailsFilter;
  onConfirm: (payload: IStoreRankDetailsFilter) => void;
  onReset: () => void;
}

const ConsoleStoreRankDetailsFilter: React.FC<IPCStoreRankDetailsFilterProps> = (props) => {
  const { style, className, regionsOptions, regionsOptionsMap, filter, onConfirm, onReset, nintendoUsOnly, isNitendo,
    sinceReleaseTime,
    earliestDataTime,
  } = props;

  // 继承逻辑在这写
  // formValue改成用ref，可提供给父组件回填时间， 或添加继承逻辑提供formValue给父组件
  const { isOperateDetail, detailFilterExtend, setDetailFilterExtend, resetDetailFilterExtend } = useFilterExtend({});

  // 筛选器的初始配置，结合继承值，渲染正确的筛选器配置（处理业务逻辑导致筛选器的差异化）
  const filterInitialInputList = useMemo(() => {
    if (!isOperateDetail) return FILTER_DEFAULT_INPUT_LIST;
    // TODO: 处理继承时，由于继承值的业务逻辑 导致筛选器的配置 跟 默认配置不一致时，根据继承值 特殊处理筛选器的初始配置
    return FILTER_DEFAULT_INPUT_LIST;
  }, [isOperateDetail]);

  // 筛选器初始值，结合继承值、默认值 综合判断
  const filterInitialValue = useMemo(() => {
    const filterDefaultComposedSelect = FILTER_DEFAULT_VALUE[FILTER_COMPOSED_SELECT]?.value;
    if (!isOperateDetail) return {
      ...FILTER_DEFAULT_VALUE,
      [FILTER_COMPOSED_SELECT]: {
        value: {
          parent: filter?.platform ?? filterDefaultComposedSelect?.parent,
          child: filter?.top ?? filterDefaultComposedSelect?.child,
        },
      },
    };
    // 继承值
    const filterExtendRangeTime = detailFilterExtend[FILTER_RANGE_TIME];
    const filterExtendComposedSelect = detailFilterExtend[FILTER_COMPOSED_SELECT]?.value;
    const filterExtendSingleAreaSelect = detailFilterExtend[SINGLE_AREA_SELECT_FILTER]?.value;
    const filterExtendAreaSelect = detailFilterExtend[FILTER_MULTIPLE_AREA_SELECT]?.value;
    const filterExtendComposedCountrySelect = detailFilterExtend[FILTER_COMPOSED_COUNTRY_SELECT]?.value;
    // 默认值
    const filterDefaultRangeTime = FILTER_DEFAULT_VALUE[FILTER_RANGE_TIME];
    const filterDefaultAreaSelect = FILTER_DEFAULT_VALUE[FILTER_MULTIPLE_AREA_SELECT]?.value;
    const filterDefaultComposedCountrySelect = FILTER_DEFAULT_VALUE[FILTER_COMPOSED_COUNTRY_SELECT]?.value;
    const filterExtendAreas = filterExtendSingleAreaSelect ? [filterExtendSingleAreaSelect] : filterExtendAreaSelect;

    // console.log(detailFilterExtend,filterExtendComposedCountrySelect)
    return {
      [FILTER_RANGE_TIME]: {
        type: filterExtendRangeTime?.filterType ?? filterDefaultRangeTime?.type,
        value: filterExtendRangeTime?.filterValue ?? filterDefaultRangeTime?.value,
      },
      [FILTER_COMPOSED_SELECT]: {
        value: {

          parent: filter?.platform ?? filterDefaultComposedSelect?.parent,
          child: filter?.top ?? filterDefaultComposedSelect?.child,
          // parent: filterExtendComposedSelect?.parent ?? filterDefaultComposedSelect?.parent,
          // child: filterExtendComposedSelect?.child ?? filterDefaultComposedSelect?.child,
        },
      },
      [FILTER_MULTIPLE_AREA_SELECT]: {
        value: filterExtendAreas ?? filterDefaultAreaSelect,
      },
      [FILTER_COMPOSED_COUNTRY_SELECT]: {
        value: {
          type: isNitendo ? 'single' : filterExtendComposedCountrySelect?.type || filterDefaultComposedCountrySelect?.type,
          countries: filterExtendComposedCountrySelect?.countries || filterDefaultComposedCountrySelect?.countries,
        },
      },
    };
  }, [isOperateDetail, detailFilterExtend, isNitendo, filter]);

  // 筛选器各个控件的value
  const [formValue, setFormValue] = useState(filterInitialValue);
  // 筛选器配置，配置筛选器有哪些控件，各个控件有哪些配置，不控制筛选器的value
  const [inputList, setInputList] = useState(filterInitialInputList);

  // 更新筛选器控件的value
  useUpdateFiltersFormValue({ isOperate: isOperateDetail, filter, regionsOptions, regionsOptionsMap, formValue, setFormValue });

  // 更新筛选器控件的配置
  useUpdateFiltersCfg({
    filter,
    regionsOptionsMap,
    inputList,
    setInputList,
    nintendoUsOnly,
    sinceReleaseTime,
    earliestDataTime,
  });

  const handleConfirm = (value: any, apiValue: any) => {
    const platform = value?.[FILTER_COMPOSED_SELECT]?.value?.parent;
    const topchart = value?.[FILTER_COMPOSED_SELECT]?.value?.child;
    const changeToNitendo = platform === 'nintendo';
    const nintendoUsOnly = changeToNitendo && topchart !== 'best sellers';
    const filterCountriesValue = value?.[FILTER_COMPOSED_COUNTRY_SELECT]?.value;
    const countriesValue = filterCountriesValue?.countries;
    const nitendoCode = nintendoUsOnly ? nitendoUSCountryCodes : nitendoCountryCodes;
    // nitendo是单选国家，且不同榜单国家数量不一样
    const nitendoCountries = (countriesValue?.length === 1 && nitendoCode?.includes(countriesValue[0])) ? countriesValue : ['us'];
    // 更新筛选器的apiValue，组合更新，即一个字段在某个条件下 需要一次性同时更新 其他多个字段的场景
    const apiValueTmp = getComposedChangedApiValue(apiValue, filter, regionsOptions, regionsOptionsMap);

    const rangeTime = apiValueTmp?.[FILTER_RANGE_TIME];
    const platformTop = apiValueTmp?.[FILTER_COMPOSED_SELECT]?.value;
    const areas = apiValueTmp?.[FILTER_MULTIPLE_AREA_SELECT]?.value;
    const countries = apiValueTmp?.[FILTER_COMPOSED_COUNTRY_SELECT]?.value;

    const filterTmp: IStoreRankDetailsFilter = {
      granularity: rangeTime?.granularity,
      start_date: rangeTime?.start_date,
      end_date: rangeTime?.end_date,

      platform: platformTop?.parent,
      top: platformTop?.child,

      regions: areas ?? [],
      markets: changeToNitendo ? nitendoCountries : countries?.countries ?? [],
    };
    onConfirm(filterTmp);
    setFormValue({
      ...formValue, ...value,
      // nintendo对国家进行特殊处理
      [FILTER_COMPOSED_COUNTRY_SELECT]: {
        value: {
          type: changeToNitendo ? 'single' : filterCountriesValue?.type,
          countries: changeToNitendo ? nitendoCountries : countries?.countries ?? [],
        },
      },
    });
    setDetailFilterExtend(apiValueTmp);
  };

  const handleReset = () => {
    setFormValue(FILTER_DEFAULT_VALUE);
    resetDetailFilterExtend();
    onReset();
  };

  return (
    <div style={{ ...(style || {}), display: 'flex', alignItems: 'baseline' }}
      className={classnames('dbi-store-rank-details-filter', className)}>
      <Filter
        showButton={false}
        inputList={inputList}
        value={formValue}
        onConfirm={handleConfirm}
        onReset={handleReset}
      />

      {/* {nintendoUsOnly &&
        <div >
          {formatMessage({ id: 'dbi_us_only' })}
        </div>
      } */}
    </div>
  );
};

export default ConsoleStoreRankDetailsFilter;
